import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BdsButton, BdsIcon } from '@/core/constants/webComponents';
import { useToast } from '@/core/components/ToastProvider/ToastProvider';
import { useMultipleEdition } from '../../contexts/MultipleEditionContext';
import { CLASSIFICATION_EVENTS } from '../../constants/events';
import { MultipleEditionSelectors } from './MultipleEditionSelectors';
import { FilteredConfirmationModal } from './FilteredConfirmationModal';
import type { BulkAnnotationObject, BulkAnnotationOperation } from '@/core/services/interfaces/BulkAnnotationInterfaces';
import type { SelectionMap } from '../../contexts/MultipleEditionContext';
import type { Sentiment, Gender } from '../ClassificationCard/types';
import './MultipleEditionModal.scss';

const POLARITY_FROM_SENTIMENT: Record<Exclude<Sentiment, null>, string> = {
  Positivo: '1',
  Negativo: '2',
  Neutro: '3',
};

const POLARITY_FILTER_API: Record<Exclude<Sentiment, null>, number> = {
  Positivo: 1,
  Negativo: 2,
  Neutro: 3,
};

const GENDER_TO_API: Record<Gender, string> = {
  man: '1',
  woman: '2',
  company: '3',
};

const buildOps = (map: SelectionMap, field: string): BulkAnnotationOperation[] =>
  Object.entries(map).map(([name, state]) =>
    state === 'add'
      ? { operation: 'add', field, new_value: name, old_value: '' }
      : { operation: 'remove', field, new_value: '', old_value: name },
  );

type FilterAnnotationPayload = {
  annotation: Record<string, unknown>;
  remove_annotation: Record<string, unknown>;
};

type FilterAnnotationOptions = {
  groupsSelection: SelectionMap;
  themesSelection: SelectionMap;
  interestsSelection: SelectionMap;
  tagsSelection: SelectionMap;
  sentimentSelection: Sentiment | null;
  genderSelection: Gender | null;
  checkedActive: boolean;
  removeActive: boolean;
  allVisiblePostsAreSpam: boolean;
};

function buildFilterAnnotation({
  groupsSelection,
  themesSelection,
  interestsSelection,
  tagsSelection,
  sentimentSelection,
  genderSelection,
  checkedActive,
  removeActive,
  allVisiblePostsAreSpam,
}: FilterAnnotationOptions): FilterAnnotationPayload {
  const annotation: Record<string, unknown> = {};
  const remove_annotation: Record<string, unknown> = {};

  const pick = (map: SelectionMap, state: 'add' | 'remove') =>
    Object.entries(map).filter(([, s]) => s === state).map(([n]) => n);

  const addGroups = pick(groupsSelection, 'add');
  const removeGroups = pick(groupsSelection, 'remove');
  const addThemes = pick(themesSelection, 'add');
  const removeThemes = pick(themesSelection, 'remove');
  const addInterests = pick(interestsSelection, 'add');
  const removeInterests = pick(interestsSelection, 'remove');
  const addTags = pick(tagsSelection, 'add');
  const removeTags = pick(tagsSelection, 'remove');

  if (addGroups.length) annotation.names = addGroups;
  if (removeGroups.length) remove_annotation.names = removeGroups;
  if (addThemes.length) annotation.themes = addThemes;
  if (removeThemes.length) remove_annotation.themes = removeThemes;
  if (addInterests.length) annotation.occupations = addInterests;
  if (removeInterests.length) remove_annotation.occupations = removeInterests;
  if (addTags.length) annotation.tags = addTags;
  if (removeTags.length) remove_annotation.tags = removeTags;
  if (sentimentSelection !== null) annotation.polarity = POLARITY_FILTER_API[sentimentSelection];
  if (genderSelection !== null) annotation.gender = GENDER_TO_API[genderSelection];
  if (checkedActive) annotation.favorite = true;
  if (removeActive) annotation.spam = !allVisiblePostsAreSpam;

  return { annotation, remove_annotation };
}

const BdsSwitch = 'bds-switch' as any;

interface MultipleEditionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const MultipleEditionModal: React.FC<MultipleEditionModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const {
    selectedPostIds,
    availablePostIds,
    selectAll,
    deselectAll,
    postsMetaMap,
    groupsSelection,
    themesSelection,
    interestsSelection,
    tagsSelection,
    setGroupsSelection,
    setThemesSelection,
    setInterestsSelection,
    setTagsSelection,
    sentimentSelection,
    genderSelection,
    setSentimentSelection,
    setGenderSelection,
    checkedActive,
    setCheckedActive,
    removeActive,
    setRemoveActive,
    totalFilteredPostCount,
    allVisiblePostsAreSpam,
    lockSelection,
    unlockSelection,
  } = useMultipleEdition();
  const switchRef = useRef<HTMLElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const dragState = useRef<{ startX: number; startY: number; initLeft: number; initTop: number; width: number; } | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const isDragging = useRef(false);
  const rafId = useRef<number | null>(null);
  const mouseHandlersRef = useRef<{ mouseMove: ((e: MouseEvent) => void) | null; mouseUp: (() => void) | null }>({
    mouseMove: null,
    mouseUp: null,
  });

  const allSelected = availablePostIds.length > 0 && availablePostIds.every((id) => selectedPostIds.has(id));
  // Set right before we imperatively assign `checked` on the bds-switch (see the effect
  // below), and cleared right after. The bds-switch's internal Stencil `@Watch('checked')`
  // re-emits `bdsChange` synchronously whenever `checked` changes, even when WE are the
  // ones setting it (e.g. after a single card was manually deselected). This flag lets the
  // listener distinguish that self-triggered echo from a real user click on the switch.
  const isSyncingSwitchRef = useRef(false);

  const removeMouseListeners = useCallback(() => {
    const { mouseMove, mouseUp } = mouseHandlersRef.current;
    if (mouseMove) {
      globalThis.removeEventListener('mousemove', mouseMove);
      mouseHandlersRef.current.mouseMove = null;
    }
    if (mouseUp) {
      globalThis.removeEventListener('mouseup', mouseUp);
      mouseHandlersRef.current.mouseUp = null;
    }
  }, []);

    const handleHeaderMouseDown = useCallback((e: React.MouseEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      removeMouseListeners();
      const dialog = dialogRef.current;
      if (!dialog) return;
      const rect = dialog.getBoundingClientRect();
      dragState.current = { startX: e.clientX, startY: e.clientY, initLeft: rect.left, initTop: rect.top, width: rect.width };
      isDragging.current = true;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isDragging.current || !dragState.current) return;

        if (rafId.current) cancelAnimationFrame(rafId.current);

        rafId.current = requestAnimationFrame(() => {
          if (!dragState.current) return;
          const dx = moveEvent.clientX - dragState.current.startX;
          const dy = moveEvent.clientY - dragState.current.startY;
          
          const headerHeightStr = getComputedStyle(document.documentElement).getPropertyValue('--mfe-header-height');
          const headerHeight = Number.parseInt(headerHeightStr, 10) || 104;
          
          let newTop = dragState.current.initTop + dy;
          let newLeft = dragState.current.initLeft + dx;
          
          const minTop = headerHeight + 8;
          const maxTop = window.innerHeight - 60; // Keep at least 60px visible at the bottom
          const minLeft = 60 - dragState.current.width; // Keep at least 60px visible on the left
          const maxLeft = window.innerWidth - 60; // Keep at least 60px visible on the right

          if (newTop < minTop) newTop = minTop;
          if (newTop > maxTop) newTop = maxTop;
          
          if (newLeft < minLeft) newLeft = minLeft;
          if (newLeft > maxLeft) newLeft = maxLeft;
          
          setPosition({
            top: newTop,
            left: newLeft,
          });
        });
      };

    const handleMouseUp = () => {
      isDragging.current = false;
      dragState.current = null;
      if (rafId.current) cancelAnimationFrame(rafId.current);
      removeMouseListeners();
    };

    mouseHandlersRef.current.mouseMove = handleMouseMove;
    mouseHandlersRef.current.mouseUp = handleMouseUp;

    globalThis.addEventListener('mousemove', handleMouseMove);
    globalThis.addEventListener('mouseup', handleMouseUp);
  }, [removeMouseListeners]);

  const handleClose = useCallback(() => {
    setPosition(null);
    setIsMinimized(false);
    if (rafId.current) cancelAnimationFrame(rafId.current);
    removeMouseListeners();
    onClose();
  }, [removeMouseListeners, onClose]);

  const handleMinimize = useCallback(() => {
    setIsMinimized((prev) => !prev);
  }, []);

  const hasAnySelection =
    Object.keys(groupsSelection).length > 0 ||
    Object.keys(themesSelection).length > 0 ||
    Object.keys(interestsSelection).length > 0 ||
    Object.keys(tagsSelection).length > 0 ||
    sentimentSelection !== null ||
    genderSelection !== null ||
    checkedActive ||
    removeActive;

  const resetSelections = useCallback(() => {
    deselectAll();
    setGroupsSelection({});
    setThemesSelection({});
    setInterestsSelection({});
    setTagsSelection({});
    setSentimentSelection(null);
    setGenderSelection(null);
    setCheckedActive(false);
    setRemoveActive(false);
  }, [deselectAll, setGroupsSelection, setThemesSelection, setInterestsSelection, setTagsSelection, setSentimentSelection, setGenderSelection, setCheckedActive, setRemoveActive]);

  const executeBulkDispatch = useCallback(async (postIds: string[]) => {
    const baseOps: BulkAnnotationOperation[] = [
      ...buildOps(groupsSelection, 'GROUP'),
      ...buildOps(themesSelection, 'THEME'),
      ...buildOps(interestsSelection, 'INTEREST'),
      ...buildOps(tagsSelection, 'TAG'),
    ];

    const amount = postIds.length;

    const hasAnySelection =
      baseOps.length > 0 ||
      sentimentSelection !== null ||
      genderSelection !== null ||
      checkedActive ||
      removeActive;

    if (!hasAnySelection) return;

    const objects: BulkAnnotationObject[] = postIds.map((postId) => {
      const meta = postsMetaMap.get(postId);
      const ops: BulkAnnotationOperation[] = [...baseOps];

      if (sentimentSelection !== null) {
        ops.push({
          operation: 'set',
          field: 'POLARITY',
          new_value: POLARITY_FROM_SENTIMENT[sentimentSelection],
          old_value: meta?.polarity ?? 0,
        });
      }
      if (genderSelection !== null) {
        ops.push({
          operation: 'set',
          field: 'GENDER',
          new_value: GENDER_TO_API[genderSelection],
          old_value: meta?.gender ?? '',
        });
      }
      if (checkedActive) {
        ops.push({ operation: 'set', field: 'CHECKED', new_value: true, old_value: meta?.checked ?? false });
      }
      if (removeActive) {
        ops.push({ operation: 'set', field: 'SPAM', new_value: !allVisiblePostsAreSpam, old_value: meta?.spam ?? false });
      }

      return {
        channel: meta?.channel ?? '',
        pid: postId,
        uid: meta?.uid ?? '',
        sac_call_id: postId,
        operations: ops,
      };
    });

    document.dispatchEvent(
      new CustomEvent(CLASSIFICATION_EVENTS.BULK_SELECTED_ANNOTATE, {
        detail: { objects, amount },
        bubbles: true,
        composed: true,
      }),
    );

    showToast({ variant: 'warning', title: t('classifyNotifications.bulkSelectedAnnotateSuccessText'), text: '', icon: 'warning' }).catch(() => {});

    for (const postId of postIds) {
      document.dispatchEvent(
        new CustomEvent(CLASSIFICATION_EVENTS.BULK_APPLY_LOCAL, {
          detail: {
            postId,
            sentiment: sentimentSelection,
            gender: genderSelection,
            checkedActive,
            removeActive,
            groups: groupsSelection,
            themes: themesSelection,
            interests: interestsSelection,
            tags: tagsSelection,
          },
          bubbles: true,
          composed: true,
        }),
      );
    }

    if (removeActive) {
      for (const postId of postIds) {
        document.dispatchEvent(
          new CustomEvent(CLASSIFICATION_EVENTS.BULK_REMOVE_FROM_SEARCH, {
            detail: { postId },
            bubbles: true,
            composed: true,
          }),
        );
      }
    }
  }, [postsMetaMap, groupsSelection, themesSelection, interestsSelection, tagsSelection, sentimentSelection, genderSelection, checkedActive, removeActive, allVisiblePostsAreSpam, showToast, t]);

  const dispatchForAllSelected = useCallback(async () => {
    await executeBulkDispatch(Array.from(selectedPostIds));
    resetSelections();
    handleClose();
  }, [selectedPostIds, executeBulkDispatch, resetSelections, handleClose]);

  const handleApplySelected = useCallback(() => {
    if (!hasAnySelection) {
      showToast({ variant: 'warning', title: t('multipleEdition.noFilterApplied'), text: '', icon: 'warning' }).catch(() => {});
      return;
    }
    if (selectedPostIds.size === 0) {
      showToast({ variant: 'warning', title: t('multipleEdition.noPostSelected'), text: '', icon: 'warning' }).catch(() => {});
      return;
    }
    dispatchForAllSelected();
  }, [hasAnySelection, selectedPostIds, dispatchForAllSelected, showToast, t]);

  const handleApplyFiltered = useCallback(() => {
    if (!hasAnySelection) {
      showToast({ variant: 'warning', title: t('multipleEdition.noFilterApplied'), text: '', icon: 'warning' }).catch(() => {});
      return;
    }
    selectAll();
    lockSelection();
    setIsConfirmOpen(true);
  }, [hasAnySelection, selectAll, lockSelection, showToast, t]);

  const dispatchForFiltered = useCallback(() => {
    unlockSelection();
    const { annotation, remove_annotation } = buildFilterAnnotation({
      groupsSelection, themesSelection, interestsSelection, tagsSelection,
      sentimentSelection, genderSelection, checkedActive, removeActive,
      allVisiblePostsAreSpam,
    });

    document.dispatchEvent(
      new CustomEvent(CLASSIFICATION_EVENTS.BULK_FILTER_ANNOTATE, {
        detail: {
          amount: totalFilteredPostCount,
          data: {
            annotation: JSON.stringify(annotation),
            remove_annotation: JSON.stringify(remove_annotation),
          },
        },
        bubbles: true,
        composed: true,
      }),
    );

    showToast({ variant: 'warning', title: t('classifyNotifications.bulkSelectedAnnotateSuccessText'), text: '', icon: 'warning' }).catch(() => {});

    // Update UI immediately for each available post on screen
    for (const postId of availablePostIds) {
      document.dispatchEvent(
        new CustomEvent(CLASSIFICATION_EVENTS.BULK_APPLY_LOCAL, {
          detail: {
            postId,
            sentiment: sentimentSelection,
            gender: genderSelection,
            checkedActive,
            removeActive,
            groups: groupsSelection,
            themes: themesSelection,
            interests: interestsSelection,
            tags: tagsSelection,
          },
          bubbles: true,
          composed: true,
        }),
      );
    }

    if (removeActive) {
      for (const postId of availablePostIds) {
        document.dispatchEvent(
          new CustomEvent(CLASSIFICATION_EVENTS.BULK_REMOVE_FROM_SEARCH, {
            detail: { postId },
            bubbles: true,
            composed: true,
          }),
        );
      }
    }

    resetSelections();
    setIsConfirmOpen(false);
    handleClose();
  }, [
    groupsSelection, themesSelection, interestsSelection, tagsSelection,
    sentimentSelection, genderSelection, checkedActive, removeActive, allVisiblePostsAreSpam,
    totalFilteredPostCount, resetSelections, handleClose, unlockSelection, availablePostIds, showToast, t,
  ]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isConfirmOpen) handleClose();
    };

    globalThis.addEventListener('keydown', handleKeyDown);
    return () => globalThis.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose, isConfirmOpen]);

  useEffect(() => {
    return () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      rafId.current = null;
      isDragging.current = false;
      dragState.current = null;
      removeMouseListeners();
    };
  }, [removeMouseListeners]);

  useEffect(() => {
    if (!isOpen) return;

    const el = switchRef.current;
    if (!el) return;

    const handleChange = (event: Event) => {
      // Ignore the echo `bdsChange` fired as a side effect of us imperatively syncing
      // `checked` below — it doesn't represent a real user click on the switch.
      if (isSyncingSwitchRef.current) return;
      const checked = (event as CustomEvent).detail?.checked;
      if (typeof checked !== 'boolean') return;
      if (checked) {
        selectAll();
      } else {
        deselectAll();
      }
    };

    el.addEventListener('bdsChange', handleChange);
    return () => el.removeEventListener('bdsChange', handleChange);
  }, [isOpen, selectAll, deselectAll, isMinimized]);

  // Imperatively (rather than declaratively via a `checked` prop) keep the switch in sync
  // with `allSelected`. This lets us bracket the assignment with `isSyncingSwitchRef` so the
  // listener above can tell a programmatic sync apart from a genuine user click. Uses
  // useLayoutEffect (runs before paint) to avoid a visible flash of the wrong state.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const el = switchRef.current as (HTMLElement & { checked?: boolean }) | null;
    if (!el) return;

    isSyncingSwitchRef.current = true;
    el.checked = allSelected;
    isSyncingSwitchRef.current = false;
  }, [isOpen, isMinimized, allSelected]);

  if (!isOpen) return null;

  return (
    <>
      <dialog
        ref={dialogRef}
        open
        className={`multiple-edition-modal${isConfirmOpen ? ' multiple-edition-modal--blocked' : ''}${isMinimized ? ' multiple-edition-modal--minimized' : ''}`}
        style={position ? { top: position.top, left: position.left, bottom: 'auto', right: 'auto' } : undefined}
        aria-modal="true"
        aria-labelledby="multiple-edition-modal-title"
      >
      <div className="multiple-edition-modal__header">
        <div
          className="multiple-edition-modal__drag-handle"
          aria-hidden="true"
          onMouseDown={handleHeaderMouseDown}
        />
        <span className="multiple-edition-modal__title" id="multiple-edition-modal-title">
          {t('multipleEdition.modalTitle')}
        </span>
        <div className="multiple-edition-modal__header-controls">
          <button type="button" className="multiple-edition-modal__minimize" onClick={handleMinimize} aria-label={isMinimized ? t('common.expand') : t('common.minimize')}>
            <BdsIcon name={isMinimized ? 'plus' : 'less'} size="small" />
          </button>
          <button type="button" className="multiple-edition-modal__close" onClick={handleClose} aria-label={t('common.close')}>
            <BdsIcon name="close" size="small" />
          </button>
        </div>
      </div>
      {!isMinimized && (
        <>
          <div className="multiple-edition-modal__select-all-row">
            <BdsSwitch ref={switchRef} name="selectAll" size="short"/>
            <span className="multiple-edition-modal__select-all-label">
              {t('multipleEdition.switchSelectAll')} ({selectedPostIds.size})
            </span>
          </div>
          <MultipleEditionSelectors />
          <div className="multiple-edition-modal__apply-section">
            <span className="multiple-edition-modal__apply-label">
              {t('multipleEdition.applyTitle')}
            </span>
            <div className="multiple-edition-modal__apply-buttons">
              <BdsButton variant="solid" size="medium" color="primary" icon="button" onClick={handleApplySelected}>
                {t('multipleEdition.applySelected')}
              </BdsButton>
              <BdsButton variant="solid" size="medium" color="primary" icon="filter" onClick={handleApplyFiltered}>
                {t('multipleEdition.applyFiltered')}
              </BdsButton>
            </div>
          </div>
        </>
      )}
      </dialog>

      <FilteredConfirmationModal
        isOpen={isConfirmOpen}
        totalPosts={totalFilteredPostCount}
        groupsSelection={groupsSelection}
        themesSelection={themesSelection}
        interestsSelection={interestsSelection}
        tagsSelection={tagsSelection}
        sentimentSelection={sentimentSelection}
        genderSelection={genderSelection}
        checkedActive={checkedActive}
        removeActive={removeActive}
        allVisiblePostsAreSpam={allVisiblePostsAreSpam}
        onCancel={() => {
          unlockSelection();
          setIsConfirmOpen(false);
          showToast({ variant: 'warning', title: t('classifyNotifications.annotationCancelled'), text: '', icon: 'info' }).catch(() => {});
        }}
        onConfirm={dispatchForFiltered}
      />
    </>
  );
};
